import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable"; // Assuming you use the standard template table
import { getAllErrorLogs } from "services/api";

// API

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString();
};

// Modal Style
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxWidth: 900,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 4,
  maxHeight: "90vh",
  overflowY: "auto",
  outline: "none",
};

function ErrorLogsPage() {
  const { state } = useLocation();
  const navigate = useNavigate();

  // Redirect if accessed directly without company context
  useEffect(() => {
    if (!state?.company) navigate("/super-admin");
  }, [state, navigate]);

  const company = state?.company || {};

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null); // For Modal
  const [searchTerm, setSearchTerm] = useState("");

  const fetchLogs = async () => {
    setLoading(true);
    if (company.tenant) {
      const data = await getAllErrorLogs(company.tenant);
      // Sort by newest first
      const sorted = Array.isArray(data)
        ? data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        : [];
      setLogs(sorted);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLogs();
  }, [company]);

  // Filter logs locally
  const filteredLogs = logs.filter(
    (log) =>
      log.errorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.errorMessage?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.employeeName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Prepare columns for DataTable
  const columns = [
    { Header: "Time", accessor: "time", width: "15%" },
    { Header: "Error Name", accessor: "errorName", width: "20%" },
    { Header: "Message", accessor: "message", width: "30%" },
    { Header: "User", accessor: "user", width: "15%" },
    { Header: "Page", accessor: "page", width: "10%" },
    { Header: "Actions", accessor: "actions", width: "10%", align: "center" },
  ];

  const rows = filteredLogs.map((log) => ({
    time: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {formatDate(log.createdAt)}
      </MDTypography>
    ),
    errorName: (
      <MDBox display="flex" alignItems="center">
        <Icon color="error" fontSize="small" sx={{ mr: 1 }}>
          error
        </Icon>
        <MDTypography variant="button" fontWeight="medium" color="error">
          {log.errorName}
        </MDTypography>
      </MDBox>
    ),
    message: (
      <MDTypography
        variant="caption"
        color="text"
        sx={{
          maxWidth: "300px",
          display: "block",
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {log.errorMessage}
      </MDTypography>
    ),
    user: (
      <MDBox display="flex" flexDirection="column">
        <MDTypography variant="caption" fontWeight="medium">
          {log.employeeName || "Unknown"}
        </MDTypography>
        <MDTypography variant="caption" color="text">
          ID: {log.employeeId}
        </MDTypography>
      </MDBox>
    ),
    page: (
      <Tooltip title={log.pageUrl || ""} placement="top">
        <MDTypography variant="caption" color="text">
          {log.pageUrl ? log.pageUrl.split("/").pop() : "-"}
        </MDTypography>
      </Tooltip>
    ),
    actions: (
      <MDButton variant="text" color="info" size="small" onClick={() => setSelectedLog(log)}>
        View Trace
      </MDButton>
    ),
  }));

  return (
    <MDBox py={3}>
      {/* Header Card */}
      <Card sx={{ mb: 3 }}>
        <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox display="flex" alignItems="center">
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
              <Icon>arrow_back</Icon>
            </IconButton>
            <MDBox>
              <MDTypography variant="h6" fontWeight="medium">
                Error Logs: {company.companyName}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Total Errors: {loading ? <CircularProgress color="info" size={10} /> : logs.length}
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDBox display="flex" gap={2}>
            <MDInput
              label="Search Logs"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <MDButton
              variant="gradient"
              color="dark"
              onClick={fetchLogs}
              startIcon={<Icon>refresh</Icon>}
            >
              Refresh
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>

      {/* Logs Table */}
      <Card>
        <MDBox p={3}>
          {loading ? (
            <MDBox display="flex" justifyContent="center" p={5}>
              <CircularProgress color="info" />
            </MDBox>
          ) : (
            <DataTable
              table={{ columns, rows }}
              showTotalEntries={true}
              isSorted={false}
              noEndBorder
              entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
            />
          )}
        </MDBox>
      </Card>

      {/* DETAIL MODAL */}
      <Modal
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <MDBox sx={modalStyle}>
          {selectedLog && (
            <>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h5" color="error">
                  {selectedLog.errorName}
                </MDTypography>
                <IconButton onClick={() => setSelectedLog(null)}>
                  <Icon>close</Icon>
                </IconButton>
              </MDBox>

              <Divider />

              <Grid container spacing={2} mb={3}>
                <Grid item xs={6}>
                  <MDTypography variant="caption" fontWeight="bold">
                    User:
                  </MDTypography>
                  <MDTypography variant="body2">
                    {selectedLog.employeeName} (ID: {selectedLog.employeeId})
                  </MDTypography>
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant="caption" fontWeight="bold">
                    Time:
                  </MDTypography>
                  <MDTypography variant="body2">{formatDate(selectedLog.createdAt)}</MDTypography>
                </Grid>
                <Grid item xs={12}>
                  <MDTypography variant="caption" fontWeight="bold">
                    Browser / Agent:
                  </MDTypography>
                  <MDTypography variant="caption" display="block" color="text">
                    {selectedLog.userAgent}
                  </MDTypography>
                </Grid>
                <Grid item xs={12}>
                  <MDTypography variant="caption" fontWeight="bold">
                    Full Page URL:
                  </MDTypography>
                  <MDTypography variant="caption" display="block" color="text">
                    {selectedLog.pageUrl}
                  </MDTypography>
                </Grid>
              </Grid>

              <MDBox bgcolor="#f8f9fa" p={2} borderRadius="lg" mb={2} border="1px solid #e0e0e0">
                <MDTypography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Error Message:
                </MDTypography>
                <MDTypography variant="body2" color="error">
                  {selectedLog.errorMessage}
                </MDTypography>
              </MDBox>

              {/* Stack Trace */}
              <MDTypography variant="subtitle2" fontWeight="bold" mt={2}>
                System Stack Trace:
              </MDTypography>
              <MDBox
                component="pre"
                bgcolor="#212121"
                color="#81c784"
                p={2}
                borderRadius="lg"
                sx={{ overflowX: "auto", fontSize: "0.75rem", maxHeight: "200px" }}
              >
                {selectedLog.errorStack || "No stack trace available."}
              </MDBox>

              {/* Component Stack */}
              {selectedLog.componentStack && (
                <>
                  <MDTypography variant="subtitle2" fontWeight="bold" mt={2}>
                    React Component Stack:
                  </MDTypography>
                  <MDBox
                    component="pre"
                    bgcolor="#eceff1"
                    color="#37474f"
                    p={2}
                    borderRadius="lg"
                    sx={{ overflowX: "auto", fontSize: "0.75rem", maxHeight: "150px" }}
                  >
                    {selectedLog.componentStack}
                  </MDBox>
                </>
              )}
            </>
          )}
        </MDBox>
      </Modal>
    </MDBox>
  );
}

export default ErrorLogsPage;
